import React, { useCallback, useEffect, useState } from 'react';
import _ from 'lodash';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'src/store/store';
import Sheet from '@amzn/meridian/sheet';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import closeTokens from '@amzn/meridian-tokens/base/icon/close-small';
import Heading from '@amzn/meridian/heading';
import {
  setIsJobConfigFilterSheetOpen,
  setJobConfigTableFilter,
} from 'src/store/jobConfigPageSlice/jobConfigPageSlice';
import FilterDropdown from 'src/components/shared/filterDropdown/filterDropdown';
import { JobConfigurationTableFilter } from 'src/store/jobConfigPageSlice/types';
import { JobConfigTableFilterInitialeState } from 'src/store/jobConfigPageSlice/constants';

// **** Components **** //

/**
 * Job Configuration Filter Sheet
 */
const JobConfigFilterSheet = () => {
  const dispatch = useDispatch();
  const { isJobConfigTableFilterSheetOpen, jobConfigTableFilterOptions, jobConfigTableFilter } = useAppSelector(
    (state) => state.jobConfigPage,
  );
  const [filter, setFilter] = useState<JobConfigurationTableFilter>(jobConfigTableFilter);
  const close = () => dispatch(setIsJobConfigFilterSheetOpen(false));
  const reset = () => setFilter(JobConfigTableFilterInitialeState());

  // Set filter in redux
  const apply = () => {
    dispatch(setJobConfigTableFilter(filter));
    dispatch(setIsJobConfigFilterSheetOpen(false));
  };

  // To listen to filter updates from other sources (i.e. ".removeTagAndUpdateFilter")
  useEffect(() => {
    if (!_.isEqual(filter, jobConfigTableFilter)) {
      setFilter(jobConfigTableFilter);
    }
  }, [jobConfigTableFilter]);

  // Update local filter when user makes a selection
  const updateFilterProp = useCallback(
    (arg: Partial<JobConfigurationTableFilter>) => {
      return setFilter({ ...filter, ...arg });
    },
    [filter],
  );

  // Return
  return (
    <Sheet type="overlay" side="end" open={isJobConfigTableFilterSheetOpen} onClose={close}>
      <Column spacing="500" height={250} minWidth={400}>
        {/* Header */}
        <Row widths={['grid-10', 'grid-2']}>
          <Heading level={4}>Filter Job Configurations</Heading>
          <Button onClick={close} type="icon">
            <Icon tokens={closeTokens}>Close sheet</Icon>
          </Button>
        </Row>

        {/* Filters */}
        <Column spacing="400">
          {/* Job Name Filter */}
          <FilterDropdown
            label="Job Name"
            value={filter.jobNames}
            options={jobConfigTableFilterOptions.jobNames}
            onChange={(v) => updateFilterProp({ jobNames: v })}
          />

          {/* Region Filter */}
          <FilterDropdown
            label="Region"
            value={filter.regions}
            options={jobConfigTableFilterOptions.regions}
            onChange={(v) => updateFilterProp({ regions: v })}
          />

          {/* Type Filter */}
          <FilterDropdown
            label="Type"
            value={filter.types}
            options={jobConfigTableFilterOptions.types}
            onChange={(v) => updateFilterProp({ types: v })}
          />

          {/* Site Type Filter */}
          <FilterDropdown
            label="Site Type"
            value={filter.siteTypes}
            options={jobConfigTableFilterOptions.siteTypes}
            onChange={(v) => updateFilterProp({ siteTypes: v })}
          />

          {/* Is Active Filter */}
          <FilterDropdown
            label="Is Active?"
            value={filter.isActiveSelections}
            options={jobConfigTableFilterOptions.isActiveSelections}
            onChange={(v) => updateFilterProp({ isActiveSelections: v })}
          />

          <br />
          <Row alignmentHorizontal="center">
            <Button type="secondary" onClick={reset}>
              Reset
            </Button>
            <Button onClick={apply}>Apply</Button>
          </Row>
        </Column>
      </Column>
    </Sheet>
  );
};

// **** Export **** //

export default JobConfigFilterSheet;
